import { create } from 'zustand';
import type { AssistantCard } from '@unipostudio/shared-types';

/** One turn of the Ask UniPostudio conversation as the panel shows it. */
export interface AskTurn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  cards: AssistantCard[];
  /** An answer that failed: shown with Try again. */
  failed?: boolean;
}

interface AskStore {
  open: boolean;
  /** A question to send as soon as the panel opens (from the palette or the Home box). */
  pending: string | null;
  turns: AskTurn[];
  openWith: (question?: string | null) => void;
  close: () => void;
  takePending: () => string | null;
  add: (turn: AskTurn) => void;
  /** Drops the last turn (a failed answer, before Try again). */
  dropLast: () => void;
  clear: () => void;
}

/**
 * Ask UniPostudio's panel state (UI v2). The conversation lives for the
 * session only: it is a working chat, not a record.
 */
export const useAskStore = create<AskStore>((set, get) => ({
  open: false,
  pending: null,
  turns: [],
  openWith: (question = null) => set({ open: true, pending: question?.trim() ? question.trim() : null }),
  close: () => set({ open: false, pending: null }),
  takePending: () => {
    const q = get().pending;
    if (q) set({ pending: null });
    return q;
  },
  add: (turn) => set((s) => ({ turns: [...s.turns, turn] })),
  dropLast: () => set((s) => ({ turns: s.turns.slice(0, -1) })),
  clear: () => set({ turns: [] }),
}));
